import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import toast from "react-hot-toast";
import {
  Database,
  Download,
  Loader2,
  RefreshCw,
  Upload,
  AlertTriangle,
  Send,
} from "lucide-react";
import { Button, Card, Input } from "../components/legacy-ui.js";
import { AppPageHeader, AppTable } from "../components/app-ui.js";
import { companyPageTitle } from "../utils/company.js";
import { LoadingState } from "../components/motion.js";
import {
  downloadBackup,
  getBackupInfo,
  restoreBackup,
  sendBackupToTelegram,
} from "../utils/api.js";

const formatCount = (n) =>
  Number(n || 0).toLocaleString("es-VE", { maximumFractionDigits: 0 });

const filenameFromDisposition = (header, fallback) => {
  if (!header) return fallback;
  const match = /filename="?([^";]+)"?/i.exec(header);
  return match?.[1] || fallback;
};

const BackupModule = () => {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [sendingTelegram, setSendingTelegram] = useState(false);
  const [file, setFile] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [fileKey, setFileKey] = useState(0);

  const fetchInfo = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getBackupInfo();
      setInfo(res.data.data);
    } catch (err) {
      toast.error(err.response?.data?.error || "No se pudo leer la base de datos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInfo();
  }, [fetchInfo]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await downloadBackup();
      const blob = new Blob([res.data], { type: "application/json" });
      const stamp = new Date().toISOString().slice(0, 10);
      const filename = filenameFromDisposition(
        res.headers?.["content-disposition"],
        `lactium-backup-${stamp}.json`,
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("Backup descargado");
    } catch (err) {
      let message = "No se pudo descargar el backup";
      const data = err.response?.data;
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          if (parsed?.error) message = parsed.error;
        } catch {
          /* keep default */
        }
      } else if (err.response?.data?.error) {
        message = err.response.data.error;
      } else if (err.message) {
        message = err.message;
      }
      toast.error(message);
    } finally {
      setDownloading(false);
    }
  };

  const handleSendTelegram = async () => {
    setSendingTelegram(true);
    try {
      const res = await sendBackupToTelegram();
      const name = res.data?.data?.filename || "archivo";
      toast.success(`Backup enviado a Telegram: ${name}`);
    } catch (err) {
      toast.error(err.response?.data?.error || "No se pudo enviar el backup a Telegram");
    } finally {
      setSendingTelegram(false);
    }
  };

  const handleRestore = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Selecciona el archivo JSON del backup");
      return;
    }
    if (confirmText.trim().toUpperCase() !== "RESTAURAR") {
      toast.error('Escribe RESTAURAR para confirmar');
      return;
    }
    setRestoring(true);
    try {
      const res = await restoreBackup(file, "RESTAURAR");
      const total = res.data?.data?.totalDocuments ?? 0;
      toast.success(`Restauración lista: ${formatCount(total)} documentos`);
      setFile(null);
      setConfirmText("");
      setFileKey((k) => k + 1);
      fetchInfo();
    } catch (err) {
      toast.error(err.response?.data?.error || "No se pudo restaurar el backup");
    } finally {
      setRestoring(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{companyPageTitle("Backup")}</title>
      </Helmet>
      <div className="app-page">
        <div className="app-section-head">
          <AppPageHeader
            title="Backup de la base de datos"
            subtitle="Respaldo manual y envío automático a Telegram cada hora."
            actions={
              <Button variant="secondary" onClick={fetchInfo} disabled={loading}>
                <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
                Actualizar
              </Button>
            }
          />
        </div>

        <div className="app-content space-y-4">
          <Card className="p-5 border border-indigo-100 bg-indigo-50/40">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl bg-indigo-600 text-white flex items-center justify-center shrink-0">
                  <Database size={22} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">Descargar backup completo</h3>
                  <p className="text-sm text-gray-600 mt-0.5">
                    Incluye clientes, facturas, pagos, leche, quesos, usuarios y el resto de
                    colecciones, con IDs y fechas. Cada hora, en punto (hora de Venezuela), se
                    envía un archivo comprimido al canal de Telegram.
                  </p>
                  {info && (
                    <p className="text-xs text-gray-500 mt-2">
                      {info.database}
                      {info.host ? ` · ${info.host}` : ""} ·{" "}
                      {formatCount(info.totalCollections)} colecciones ·{" "}
                      {formatCount(info.totalDocuments)} documentos
                    </p>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
              <Button onClick={handleDownload} disabled={downloading || loading}>
                {downloading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Download size={16} />
                )}
                {downloading ? "Generando…" : "Descargar JSON"}
              </Button>
              <Button
                variant="secondary"
                onClick={handleSendTelegram}
                disabled={sendingTelegram || loading}
              >
                {sendingTelegram ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Send size={16} />
                )}
                {sendingTelegram ? "Enviando…" : "Enviar a Telegram"}
              </Button>
              </div>
            </div>
          </Card>

          {loading ? (
            <LoadingState count={5} label="Cargando backup" />
          ) : (
            <AppTable
              columns={[
                { key: "name", title: "Colección" },
                { key: "count", title: "Documentos", align: "right" },
              ]}
            >
              {(info?.collections || []).map((col) => (
                <tr key={col.name}>
                  <td className="font-medium text-gray-800">{col.name}</td>
                  <td className="text-right tabular-nums">{formatCount(col.count)}</td>
                </tr>
              ))}
              {!info?.collections?.length && (
                <tr>
                  <td colSpan={2} className="text-center text-gray-500 py-8">
                    No hay colecciones para respaldar
                  </td>
                </tr>
              )}
            </AppTable>
          )}

          <Card className="p-5 border border-amber-200 bg-amber-50/50">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle size={20} className="text-amber-600 shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-gray-900">Restaurar en el servidor actual</h3>
                <p className="text-sm text-gray-600 mt-0.5">
                  Reemplaza las colecciones del archivo en MongoDB. Úsalo cuando la API ya
                  apunte al servidor nuevo. Esta acción no se puede deshacer.
                </p>
              </div>
            </div>
            <form onSubmit={handleRestore} className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Archivo de backup (.json o .json.gz)
                </label>
                <input
                  key={fileKey}
                  type="file"
                  accept=".json,.gz,application/json,application/gzip"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-white file:text-sm file:font-medium file:border file:border-gray-300"
                />
                {file && (
                  <p className="text-xs text-gray-500 mt-1">
                    {file.name} · {(file.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                )}
              </div>
              <Input
                label='Escribe RESTAURAR para confirmar'
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="RESTAURAR"
                autoComplete="off"
              />
              <Button
                type="submit"
                disabled={restoring}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {restoring ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Upload size={16} />
                )}
                {restoring ? "Restaurando…" : "Restaurar backup"}
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </>
  );
};

export default BackupModule;
